import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from '@mui/material'
import useFavurTranslation from 'hooks/useFavurTranslation'
import { sortBy } from 'lodash/fp'
import useRequestEmployeeData from 'pages/RequestEmployeeData/useRequestEmployeeData'
import type { PersonWithWorkflow } from 'pages/RequestEmployeeData/useRequestEmployeeData'
import type { RadioFormGroupProps } from 'redesign/components/molecules/RadioFormGroup/types'
import type { EmployeeSearchProps } from 'redesign/components/organisms/employee-search/types'
import { formatDate } from 'redesign/shared/date'
import { DATE_FORMAT_VARIANTS } from 'redesign/shared/date/constants'
import { ONE } from 'shared/constants'
import type { PersonT } from 'types'
import { newDateWithoutTime } from 'utils/date'
import { getNormalizedFirstName, getNormalizedLastName, getSortingLastName, normalizeText } from 'utils/person'

export const useRequestUserData = () => {
  const { palette } = useTheme()
  const { t, locale } = useFavurTranslation()
  const [members, setMembers] = useState<Map<number, boolean>>(new Map())
  const [showConfirmForm, setShowConfirmForm] = useState<boolean>(false)
  const {
    loading,
    allPersons,
    tenantList,
    fetchTenantPersons,
    selectedTenant: theSelectedTenant,
  } = useRequestEmployeeData()

  const tenantSelectOptions = useMemo(
    () =>
      tenantList.map((tenant, index) => ({
        id: index,
        label: tenant.name,
        value: index,
        key: tenant.id,
      })),
    [tenantList],
  )

  const selectedTenant = useMemo(
    () => tenantSelectOptions.findIndex(({ key }) => key === theSelectedTenant?.id),
    [tenantSelectOptions, theSelectedTenant?.id],
  )

  const onTenantChange = useCallback(
    (value: number) => {
      const newTenant = tenantSelectOptions[value]

      fetchTenantPersons(newTenant.key)
    },
    [fetchTenantPersons, tenantSelectOptions],
  )

  const currentSelectedPersons = useMemo(
    () =>
      Array.from(members).reduce<number[]>((acc, [id, checked]) => {
        if (checked) {
          return [...acc, id]
        }

        return acc
      }, []),
    [members],
  )
  const requestedUsersAmount = currentSelectedPersons?.length
  const areOptionsSelected = Boolean(requestedUsersAmount)

  const onChange = useCallback(
    ({ id }: { id: number }) => {
      setMembers((curr) => new Map(curr).set(id, !curr.get(id)))
    },
    [setMembers],
  )
  const getInfoText = useCallback(
    (person: PersonT) => {
      const personWithWorkflow = person as PersonWithWorkflow
      const { shareUserDataTask, lastSharedData } = personWithWorkflow

      if (shareUserDataTask) {
        return t('requestEmployeeData.single.subTitle.alreadyRequested', {
          date: formatDate({
            date: newDateWithoutTime(shareUserDataTask.insertedAt),
            t,
            dateFormat: DATE_FORMAT_VARIANTS.shortWithoutWeekday,
            locale,
          }),
        })
      }

      return lastSharedData
        ? t('requestEmployeeData.single.subTitle.lastShared', {
            lastSharedData: formatDate({
              date: newDateWithoutTime(lastSharedData),
              t,
              dateFormat: DATE_FORMAT_VARIANTS.shortWithoutWeekday,
              locale,
            }),
          })
        : t('requestEmployeeData.single.subTitle.noData')
    },
    [locale, t],
  )
  const employees = useMemo(
    () =>
      sortBy(
        (p) => getSortingLastName(p),
        allPersons.map((person) => ({
          ...person,
          subtitle: getInfoText(person),
          disabled: person.shareUserDataTask !== undefined,
        })),
      ),
    [allPersons, getInfoText],
  )
  const [filteredEmployeeList, setFilteredEmployeeList] = useState<PersonT[]>(employees)
  const [searchText, setSearchText] = useState<string>('')
  const employeeList = useMemo(
    () =>
      employees.map((person) => ({
        firstName: getNormalizedFirstName(person),
        lastName: getNormalizedLastName(person),
        person,
      })),
    [employees],
  )

  const filterEmployees = useCallback(
    (text: string) => {
      const normalizedText = normalizeText(text)
      const filteredEmployees = employeeList
        .filter(
          (normPerson) => normPerson.firstName.includes(normalizedText) || normPerson.lastName.includes(normalizedText),
        )
        .map((normPerson) => normPerson.person)

      setFilteredEmployeeList(filteredEmployees)
    },
    [employeeList],
  )

  const handleSelectAll = useCallback(() => {
    const value = areOptionsSelected ? false : true

    const enabledEmployees = employees.filter((employee) => !employee.disabled)
    const newMembers = new Map()

    enabledEmployees.forEach((employee) => setMembers(newMembers.set(employee.id, value)))
  }, [areOptionsSelected, employees])

  useEffect(() => {
    filterEmployees(searchText)
  }, [filterEmployees, searchText])

  const showTenantSelect = tenantList.length > ONE

  const tenantSelectRadioProps = useMemo<RadioFormGroupProps>(
    () => ({
      options: tenantSelectOptions,
      setSelectedValue: onTenantChange,
      selectedValue: selectedTenant,
    }),
    [onTenantChange, selectedTenant, tenantSelectOptions],
  )

  const employeeSearchProps = useMemo<EmployeeSearchProps>(
    () => ({
      filteredPersonsList: filteredEmployeeList,
      onChange: (e) => setSearchText(e.target.value),
      clearText: () => setSearchText(''),
      searchText,
      currentSelectedPersons,
      onClick: (option) => onChange({ id: option.id as number }),
      handleSelectAll: handleSelectAll,
      areOptionsSelected: areOptionsSelected,
    }),
    [areOptionsSelected, currentSelectedPersons, filteredEmployeeList, handleSelectAll, onChange, searchText],
  )

  return {
    loading,
    palette,
    t,
    employeeSearchProps,
    tenantSelectRadioProps,
    showTenantSelect,
    showConfirmForm,
    setShowConfirmForm,
    requestedUsersAmount,
  }
}
